/**
 * 题4 sh2026q23 — 2026 上海中考 Q23「新定义：派生点与派生直线」。
 *
 * 抛物线 y=ax²+bx+c 的对称轴交 x 轴于 A；A 右移 1 得 B；C 与 B 同横坐标、
 * 纵坐标 2a，C 为「派生点」，直线 AC 为「派生直线」。
 *
 * 全部由公式推导：A 由对称轴 x=-b/2a；C 由定义；派生直线由两点式；
 * 第(2)问由派生直线反解 A、C、P、Q，由 2a=C.y 得 a，由 P 代入得 k；
 * CP、CQ 由距离公式。
 */

import {
  axisOf,
  dist,
  evalLine,
  evalQuad,
  lineThrough,
  pt,
  quadFromVertex,
  xInterceptOf,
} from "../math/func";
import type { Line2, Quad } from "../math/func";
import type { ProblemDef, SceneDef } from "./types";

/* Scene ①：y = 2x² - c（作图取 c=0），a=2，b=0。 */
const quad1: Quad = { a: 2, b: 0, c: 0 };
const A1 = pt(axisOf(quad1), 0); // (0, 0)
const B1 = pt(A1.x + 1, 0); // (1, 0)
const C1 = pt(B1.x, 2 * quad1.a); // (1, 4)
/** 派生直线 y = 4x（与 c 无关）。 */
export const derivedLine26a: Line2 = lineThrough(A1, C1);

/* Scene ②：派生直线 y = 2x - 6。 */
export const derivedLine26b: Line2 = { k: 2, b: -6 };
const A2 = pt(xInterceptOf(derivedLine26b), 0); // (3, 0)
const B2 = pt(A2.x + 1, 0); // (4, 0)
const C2 = pt(B2.x, evalLine(derivedLine26b, B2.x)); // (4, 2)
const P2 = pt(1, evalLine(derivedLine26b, 1)); // (1, -4)
const Q2 = pt(7, evalLine(derivedLine26b, 7)); // (7, 8)
/* 由定义：对称轴 x = A.x = 3，2a = C.y → a = 1；y=(x-3)²+k 过 P → k=-8。 */
const a2 = C2.y / 2; // 1
const k2 = P2.y - a2 * (P2.x - A2.x) * (P2.x - A2.x); // -8
/** 抛物线 y = (x-3)² - 8 = x² - 6x + 1。 */
export const parabola26: Quad = quadFromVertex(a2, pt(A2.x, k2));
const cpLen = dist(C2, P2); // 3√5
const cqLen = dist(C2, Q2); // 3√5

const scene1: SceneDef = {
  id: "s1",
  tab: "① y=2x²-c 的派生直线",
  caption:
    "a=2，对称轴 x=0：A(0,0)、B(1,0)、C(1,2a)=(1,4)。派生直线 AC：y=4x" +
    "（斜率恒为 4，与 c 无关；图中取 c=0 画抛物线 y=2x²）。",
  view: { xMin: -2, xMax: 3, yMin: -1, yMax: 6 },
  curves: [
    { kind: "quad", id: "parabola", quad: quad1, tone: "cyan" },
    { kind: "line", id: "derived", line: derivedLine26a, tone: "gold" },
  ],
  points: [
    { name: "A", at: A1, text: "A(O)", dx: -14, dy: 16 },
    { name: "B", at: B1, showCoords: true, dx: 4, dy: 16 },
    { name: "C", at: C1, emphasis: true, showCoords: true, dx: 12, dy: -8 },
  ],
  segments: [{ a: B1, b: C1, tone: "dim", dashed: true }],
};

const scene2: SceneDef = {
  id: "s2",
  tab: "② CP/CQ 与 Q 的位置",
  caption:
    "派生直线 y=2x-6 交 x 轴于 A(3,0)：B(4,0)、C(4,2)、P(1,-4)、Q(7,8)。" +
    "CP=CQ=3√5 → CP/CQ=1。对称轴 x=3、a=1，y=(x-3)²-8 过 P；x=7 时 y=8，Q 在抛物线上。",
  view: { xMin: -1, xMax: 8.5, yMin: -9.5, yMax: 9.5 },
  curves: [
    { kind: "quad", id: "parabola", quad: parabola26, tone: "cyan" },
    { kind: "line", id: "derived", line: derivedLine26b, tone: "gold" },
    { kind: "vline", id: "axis", x: A2.x, tone: "dim", dashed: true },
  ],
  points: [
    { name: "A", at: A2, showCoords: true, dx: -4, dy: 16 },
    { name: "B", at: B2, dx: 14, dy: 12 },
    { name: "C", at: C2, showCoords: true, dx: 14, dy: -6 },
    { name: "P", at: P2, showCoords: true, dx: -10, dy: 18 },
    { name: "Q", at: Q2, emphasis: true, showCoords: true, dx: -14, dy: -14 },
  ],
  segments: [{ a: B2, b: C2, tone: "dim", dashed: true }],
};

export const sh2026q23: ProblemDef = {
  id: "sh2026q23",
  title: "2026·Q23 派生点与派生直线",
  statement:
    "抛物线 y=ax²+bx+c 的对称轴交 x 轴于 A，将 A 向右平移 1 个单位得 B；" +
    "C 与 B 横坐标相同且纵坐标为 2a，则 C 称为抛物线的「派生点」，直线 AC 称为「派生直线」。" +
    "（1）若抛物线为 y=2x²-c，求其派生直线的表达式；（2）已知派生直线为 y=2x-6，" +
    "抛物线与其公共点为 P(1,m)，Q(7,n) 为派生直线上一点，求 CP/CQ 的值，并判断 Q 是否在该抛物线上。",
  givens: [
    { label: "C 纵坐标", value: "2a" },
    { label: "AB", value: "1" },
    { label: "(2) 派生直线", value: "y = 2x - 6" },
  ],
  scenes: [scene1, scene2],
  solution: [
    "（1）y=2x²-c：a=2，对称轴 x=0，A(0,0)，B(1,0)，C(1,4)。" +
      "设派生直线 y=kx，代入 C(1,4) 得 k=4，即派生直线为 y=4x。",
    "（2）派生直线 y=2x-6 交 x 轴于 A(3,0)，故 B(4,0)，C(4,2)；" +
      "P(1,-4)，Q(7,8)。PC=√((4-1)²+(2-(-4))²)=3√5，CQ=3√5，故 CP/CQ=1。",
    "由定义，对称轴为 x=3，2a=2 得 a=1。设 y=(x-3)²+k，代入 P(1,-4) 得 k=-8，" +
      "即 y=(x-3)²-8=x²-6x+1。x=7 时 y=49-42+1=8，与 Q 的纵坐标相等，故 Q 在该抛物线上。",
  ],
  answer: "① y=4x ｜ ② CP/CQ=1，Q 在抛物线上",
  computed: {
    derivedK: derivedLine26a.k,
    derivedB: derivedLine26a.b,
    ax: A2.x,
    a: a2,
    quadB: parabola26.b,
    quadC: parabola26.c,
    cp: cpLen,
    cq: cqLen,
    ratio: cpLen / cqLen,
    qy: Q2.y,
    quadAtQ: evalQuad(parabola26, Q2.x),
    quadAtP: evalQuad(parabola26, P2.x),
  },
};
